import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';

import { AppDataService } from 'src/app/services/data/app-data.service';
import { AclService } from '../../../services/acl.service';
import { EnumCurrency } from '@simplyfly/shared';
import { IFlightBriefCatering } from '@simplyfly/shared';
import { Subscription } from 'rxjs';
import { EnumAcl } from '@simplyfly/shared';
import { EnumLanguage } from '@simplyfly/shared';
import { getLanguageLabel } from 'src/app/enums/language.helpers';
import { FlightBriefCateringService } from 'src/app/services/flight-brief-caterings/flight-brief-caterings.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { BreadcrumbsService } from 'src/app/services/breadcrumbs/breadcrumbs.service';
import { EnumEnquiryType } from '@simplyfly/shared';
import { getEnumEnquiryTypeLabel } from 'src/app/enums/enquiry-type.helpers';
import { AuthService } from 'src/app/services/auth/auth.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-flight-brief-catering-edit',
    templateUrl: './flight-brief-catering-edit.component.html',
    styleUrls: ['./flight-brief-catering-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, RouterLink, TranslateModule]
})
export class FlightBriefCateringEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inModal = false;
  @Input() flightBriefCateringId: string | null = null;

  @Output() dismissModal: EventEmitter<string | null> = new EventEmitter();

  getLanguageLabel = getLanguageLabel;
  getEnumEnquiryTypeLabel = getEnumEnquiryTypeLabel;

  isLogged = false;
  form: FormGroup = this.resetForm();
  flightBriefCatering: IFlightBriefCatering | null = null;

  private subscriptions = new Subscription();
  private flightBriefCateringSubscription: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private appDataService: AppDataService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private aclService: AclService,
    private flightBriefCateringService: FlightBriefCateringService,
    private loaderService: LoaderService,
    private breadcrumbsService: BreadcrumbsService
  ) {
    this.authService.isLoggedObservable.subscribe(
      (isLogged: boolean) => (this.isLogged = isLogged)
    );
  }

  get textByLanguage(): FormGroup {
    return this.form.get('textByLanguage') as FormGroup;
  }

  getTextForLanguage(language: EnumLanguage): FormControl {
    return this.textByLanguage.get(language) as FormControl;
  }

  get enquiryTypes(): FormControl {
    return this.form.get('enquiryTypes') as FormControl;
  }

  getEnquiryTypes(): EnumEnquiryType[] {
    return Object.values(EnumEnquiryType);
  }

  ngOnInit() {
    this.form.disable();

    this.activatedRoute.params.subscribe(async () => {
      if (!this.flightBriefCateringId) {
        this.flightBriefCateringId =
          this.activatedRoute.snapshot.paramMap.get('flightBriefCateringId');
      }

      if (this.flightBriefCateringId) {
        if (!this.inModal) {
          await this.aclService.checkAclAccess(EnumAcl.flightBriefCateringsEdit);
        }
        this.loadData();
      } else {
        if (!this.inModal) {
          await this.aclService.checkAclAccess(EnumAcl.flightBriefCateringsAdd);
        }

        this.resetForCreate();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.flightBriefCateringId) {
      if (this.flightBriefCateringId) {
        this.loadData();
      } else {
        this.resetForCreate();
      }
    }
  }

  ngOnDestroy(): void {
    this.flightBriefCateringSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  getCurrencies(): EnumCurrency[] {
    return Object.values(EnumCurrency);
  }

  getLanguages(): EnumLanguage[] {
    return Object.values(EnumLanguage);
  }

  loadData(): void {
    if (!this.flightBriefCateringId) {
      return;
    }

    if (this.isLogged) {
      this.loadFlightBriefCatering();
    } else {
      setTimeout(() => {
        this.loadData();
      }, 500);
    }
  }

  loadFlightBriefCatering(): void {
    const requestedId = this.flightBriefCateringId;

    if (!requestedId) {
      return;
    }

    this.flightBriefCateringSubscription?.unsubscribe();
    this.flightBriefCateringSubscription = this.flightBriefCateringService
      .getFromId(requestedId)
      .subscribe((flightBriefCatering: IFlightBriefCatering) => {
        if (this.flightBriefCateringId !== requestedId) {
          return;
        }

        this.flightBriefCatering = flightBriefCatering;

        if (this.flightBriefCatering) {
          this.breadcrumbsService.setCurrentItem({
            id: this.flightBriefCatering.id,
            text: this.flightBriefCatering.textByLanguage
              ? this.flightBriefCatering.textByLanguage[EnumLanguage.fr]
              : ''
          });
        }

        this.setValueToForm();
      });
  }

  setValueToForm(): void {
    if (this.form && this.flightBriefCatering) {
      this.form = this.resetForm();

      for (const field in this.form.value) {
        if (typeof this.flightBriefCatering[field] !== 'undefined') {
          this.form.get(field).setValue(this.flightBriefCatering[field]);
        }
      }

      this.form.enable();
    }
  }

  submitForm(): void {
    this.form.markAsTouched();

    if (this.form.valid) {
      this.loaderService.presentLoader();

      let data = Object.assign({}, this.form.value);

      for (const field in data) {
        if (typeof data[field] === 'undefined') {
          data[field] = null;
        }
      }

      this.form.enable();

      data = this.fixUndefinedValues(data);

      let promise;
      if (this.flightBriefCateringId) {
        data.id = this.flightBriefCateringId;
        promise = () => this.flightBriefCateringService.update(data);
      } else {
        promise = () => this.flightBriefCateringService.create(data);
      }

      promise()
        .then(async id => {
          await this.loaderService.hideLoaderOnSuccess();

          if (!this.flightBriefCateringId) {
            this.flightBriefCateringId = id;

            if (this.inModal) {
              this.closeModal(this.flightBriefCateringId);
            }
          } else {
            if (this.inModal) {
              this.closeModal();
            }
          }

          if (!this.inModal) {
            this.redirectAfterSaving();
          }
        })
        .catch(async (err: any) => {
          await this.loaderService.hideLoaderOnFailure(err.message);
        })
        .finally(() => {
          this.form.enable();
        });
    }
  }

  fixUndefinedValues(data: IFlightBriefCatering): IFlightBriefCatering {
    for (const field in data) {
      if (Array.isArray(data[field]) || typeof data[field] === 'object') {
        data[field] = this.fixUndefinedValues(data[field]);
      } else if (typeof data[field] === 'undefined') {
        data[field] = null;
      }
    }

    return data;
  }

  redirectAfterSaving(): void {
    this.router.navigate(['/admin/flight-brief-caterings/' + this.flightBriefCateringId]);
  }

  resetForm(): FormGroup {
    const textByLanguageFormGroup: FormGroup = new FormGroup({});

    for (const language of Object.values(EnumLanguage)) {
      textByLanguageFormGroup.addControl(language, new FormControl(null, Validators.required));
    }

    return new FormGroup({
      textByLanguage: textByLanguageFormGroup,
      position: new FormControl(1, [Validators.required]),
      enquiryTypes: new FormControl([])
    });
  }

  private resetForCreate(): void {
    this.flightBriefCateringSubscription?.unsubscribe();
    this.flightBriefCateringSubscription = null;
    this.flightBriefCatering = null;
    this.form = this.resetForm();
    this.enquiryTypes.setValue(this.getEnquiryTypes());
    this.enquiryTypes.updateValueAndValidity();
    this.form.enable();
  }

  closeModal(newId: string | null = null): void {
    this.dismissModal.emit(newId);
  }

  toggleEnquiryType(enquiryType: EnumEnquiryType): void {
    const values: EnumEnquiryType[] = [...this.form.value.enquiryTypes];
    const index: number = values.indexOf(enquiryType);

    if (index === -1) {
      values.push(enquiryType);
    } else {
      values.splice(index, 1);
    }

    this.form.get('enquiryTypes').setValue(values);
    this.form.get('enquiryTypes').updateValueAndValidity();
  }

  toggleAllEnquiryType(): void {
    const values: EnumEnquiryType[] = [];

    if (this.form.value.enquiryTypes.length !== this.getEnquiryTypes().length) {
      for (const enquiryType of this.getEnquiryTypes()) {
        values.push(enquiryType);
      }
    }

    this.form.get('enquiryTypes').setValue(values);
    this.form.get('enquiryTypes').updateValueAndValidity();
  }
}
